// Lager (/rechnung/lager, nur Billtoro; server/src/modules/rechnungstool/lager-routes.ts): Antworten des Servers,
// Adressen und Texte (ohne Oberfläche). Mengen kommen in der Einheit des Artikels (bis 3 Nachkommastellen); den
// Bestand rechnet nur der Server (Summe aller Bewegungen).
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { deQty } from '../formular';
import { fehlerText } from '../tarifHinweis';

export type LagerArt = 'zugang' | 'abgang' | 'zaehlung' | 'rechnung' | 'lieferschein' | 'storno';

// Artikel in der Lagerliste (nur Artikel mit „Bestand führen“)
export interface LagerArtikel {
  id: string;
  artikelnummer: string | null;
  name: string;
  unit: string;
  mindest: number | null;
  bestand: number;
  // Tag der letzten Bewegung
  letzte: string | null;
  // unter dem Mindestbestand oder unter 0
  wenig: boolean;
}

export interface LagerListe {
  artikel: LagerArtikel[];
  wenig: number;
}

export interface Bewegung {
  id: string;
  nr: number;
  datum: string;
  art: LagerArt;
  // mit Vorzeichen (+ ins Lager, − heraus); bei der Zählung die Differenz
  menge: number;
  gezaehlt: number | null;
  einheit: string;
  notiz: string | null;
  // Beleg, der gebucht hat (Rechnung, Stornorechnung, Lieferschein)
  beleg: { art: 'rechnung' | 'storno' | 'lieferschein'; id: string; number: string } | null;
  // Gegenbuchung: Art und Nr der Buchung, die sie zurücknimmt
  von: { art: LagerArt; nr: number } | null;
  zurueckgenommen: boolean;
  // „Rückgängig“ möglich (Zugang, Abgang, Zählung von Hand, noch nicht zurückgenommen, mit Änderung)
  rueckgaengig: boolean;
  bestandDanach: number;
  // Zählung: danach kamen Belege mit Datum bis zum Zähltag dazu
  abweichung: boolean;
  // Zugang aus einer Ausgabe; storniert/geändert: die Ausgabe wurde danach storniert bzw. geändert (bucht nichts zurück)
  ausgabe: { id: string; nummer: string; storniert: boolean; geaendert: boolean } | null;
}

export interface Verlauf {
  artikel: {
    id: string;
    artikelnummer: string | null;
    name: string;
    unit: string;
    // false: „Bestand führen“ ausgeschaltet, nur der Verlauf bleibt
    lager: boolean;
    entfernt: boolean;
    mindest: number | null;
    bestand: number;
    wenig: boolean;
  };
  bewegungen: Bewegung[];
  // ältere Bewegungen gibt es noch (?vor=<kleinste nr>)
  weitere: boolean;
  // Haken wieder eingeschaltet, dazwischen kamen Belege mit dem Artikel: „Bitte neu zählen.“
  neuZaehlen: boolean;
}

// Antwort von GET …/lager/vorschlag (Zugang aus einer Ausgabe, server/src/modules/rechnungstool/lager-ausgabe.ts)
export interface PositionVorschlag {
  // Position in der E-Rechnung (ab 1)
  pos: number;
  text: string;
  // > 0, höchstens drei Nachkommastellen
  menge: number;
  // Code wie gelesen (H87, KGM …) und als Text („Stück“, „kg“); ohne Text (unbekannter Code) nur die Menge
  einheit: string | null;
  einheitText: string | null;
  artikelnummer: string | null;
  kaeuferArtikelnummer: string | null;
  // gleiche Artikelnummer, sonst gleicher Name; nur ein Vorschlag
  vorschlag: { artikelId: string; grund: 'nummer' | 'name' } | null;
  // false: Einheit der Rechnung passt nicht zum Artikel (Menge selbst eintragen); null: kein Vorschlag oder keine Einheit
  einheitPasst: boolean | null;
  hinweis: string | null;
}
export interface AusgabeVorschlag {
  ausgabe: {
    id: string;
    nummer: string;
    partner: string;
    belegnummer: string | null;
    belegdatum: string;
    kategorie: string;
    storniert: boolean;
    gutschrift: boolean;
  };
  // der Beleg ist eine lesbare E-Rechnung
  erechnung: boolean;
  // Vorschlag für das Datum (Lieferdatum, sonst Belegdatum) und die Notiz
  datum: string;
  notiz: string;
  // schon aus dieser Ausgabe gebucht (und nicht zurückgenommen)
  gebucht: { nr: number; datum: string; artikel: string; menge: number; einheit: string }[];
  positionen: PositionVorschlag[];
  hinweise: string[];
}

const API = '/api/rechnungstool';
export const lagerUrl = (firmId: string) => `${API}/${firmId}/lager`;
export const verlaufUrl = (artikelId: string, vor?: number) => `${API}/lager/${artikelId}${vor ? `?vor=${vor}` : ''}`;
export const zaehlungUrl = (firmId: string, datum?: string) => `${API}/${firmId}/lager/zaehlung${datum ? `?datum=${datum}` : ''}`;
export const buchenUrl = (firmId: string) => `${API}/${firmId}/lager/buchen`;
export const vorschlagUrl = (firmId: string, ausgabeId: string) => `${API}/${firmId}/lager/vorschlag?ausgabe=${encodeURIComponent(ausgabeId)}`;
export const rueckgaengigUrl = (id: string) => `${API}/lager/bewegung/${id}/storno`;
export const bestandCsvUrl = (firmId: string, stichtag: string) => `${API}/${firmId}/lager/bestand.csv?stichtag=${stichtag}`;
export const bewegungenCsvUrl = (firmId: string, von: string, bis: string) => `${API}/${firmId}/lager/bewegungen.csv?von=${von}&bis=${bis}`;

// Satz oben auf der Lagerseite und beim Haken „Bestand führen“ (wahr und vollständig, lager.md 3)
export const LAGER_SATZ =
  'Der Bestand sinkt einmal je Lieferung: mit dem Lieferschein, sonst mit der Rechnung. Ein Storno legt die Menge wieder dazu. Rechnungen gehen auch bei zu wenig Bestand.';

// Einheit der Position passt zum Artikel (wie der Server, lager.ts gleicheEinheit: ohne Leerraum am Rand, groß/klein egal)
export const gleicheEinheit = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

// „12 Stück“, „2,5 kg“, „−4 Stück“ (echtes Minuszeichen)
export const mengeText = (menge: number, einheit: string) => `${menge < 0 ? '−' : ''}${deQty(Math.abs(menge))}${einheit ? ` ${einheit}` : ''}`;
// mit Vorzeichen für den Verlauf: „+12“, „−4“, „±0“
export const mengeVorzeichen = (menge: number) => (menge > 0 ? `+${deQty(menge)}` : menge < 0 ? `−${deQty(-menge)}` : '±0');
export const artikelText = (n: number) => (n === 1 ? '1 Artikel' : `${n} Artikel`);

const ART_TEXT: Record<LagerArt, string> = {
  zugang: 'Zugang',
  abgang: 'Abgang',
  zaehlung: 'Zählung',
  rechnung: 'Rechnung',
  lieferschein: 'Lieferschein',
  storno: 'Storno',
};
// Art im Verlauf: Beleg mit Nummer („Rechnung RE-2026-0012“), Gegenbuchung eines Belegs „Storno …“, von Hand
// zurückgenommen „Rückgängig (Zugang Nr. 4)“, aus einer Ausgabe „Zugang aus Ausgabe A-2026-0012“
export function artText(b: Pick<Bewegung, 'art' | 'beleg' | 'von' | 'ausgabe'>): string {
  if (b.art === 'storno') {
    if (b.beleg) return b.beleg.art === 'lieferschein' ? `Storno Lieferschein ${b.beleg.number}` : `Storno ${b.beleg.number}`;
    return b.von ? `Rückgängig (${ART_TEXT[b.von.art]} Nr. ${b.von.nr})` : 'Rückgängig';
  }
  if (b.beleg) return `${ART_TEXT[b.art]} ${b.beleg.number}`;
  if (b.art === 'zugang' && b.ausgabe) return `Zugang aus Ausgabe ${b.ausgabe.nummer}`;
  return ART_TEXT[b.art];
}

// Datum für Buchungen von Hand: vom 1. Januar des Vorjahres bis heute (wie der Server)
export const fruehestes = (heute: string) => `${Number(heute.slice(0, 4)) - 1}-01-01`;

// Lagerliste der Firma laden; laden() lädt neu (nach dem Buchen). Antworten einer vorher gewählten Firma verwirft er.
export function useLager(firmId: string) {
  const [liste, setListe] = useState<LagerListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const aktuell = useRef(firmId);
  aktuell.current = firmId;
  const laden = useCallback(async () => {
    try {
      const l = await api.get<LagerListe>(lagerUrl(firmId));
      if (aktuell.current !== firmId) return;
      setListe(l);
      setFehler(null);
    } catch (err) {
      if (aktuell.current === firmId) setFehler(fehlerText(err));
    }
  }, [firmId]);
  useEffect(() => {
    setListe(null);
    void laden();
  }, [laden]);
  return { liste, fehler, laden };
}
export type Lager = ReturnType<typeof useLager>;

// Suche in der Lagerliste: Name oder Artikelnummer
export function passtArtikel(a: Pick<LagerArtikel, 'name' | 'artikelnummer'>, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return a.name.toLowerCase().includes(t) || !!a.artikelnummer?.toLowerCase().includes(t);
}
